import { normalizeMessageEnvelope } from "@wakeloop/schemas";
import type {
  MessageEnvelopeTargetField,
  SpaceMemberDirectoryEntry,
  WakeLoopSpacePostMessageAction,
} from "@wakeloop/schemas/types";

export type DispatchPostMessageInputKind =
  | "direct_payload"
  | "fenced_block"
  | "trailing_json";

export type DispatchSendTargetProjectionEntry = SpaceMemberDirectoryEntry;

export type DispatchSendTargetReferenceLoadResult =
  | {
      status: "loaded";
      projection: DispatchSendTargetProjectionEntry[];
      referenceLines: string[];
    }
  | {
      status: "failed";
      errorMessage: string;
      projection: null;
      referenceLines: [];
    };

type TargetValidationRule = {
  fieldName: MessageEnvelopeTargetField;
  relationName: "wake" | "reference" | "cc";
};

const TARGET_VALIDATION_RULES: TargetValidationRule[] = [
  {
    fieldName: "activationTargets",
    relationName: "wake",
  },
  {
    fieldName: "referenceTargets",
    relationName: "reference",
  },
  {
    fieldName: "ccTargets",
    relationName: "cc",
  },
];

export function normalizeDispatchPostMessageAction(input: {
  action: WakeLoopSpacePostMessageAction;
  currentReplyToSignalId: string | null;
  inputKind: DispatchPostMessageInputKind;
  sendTargetReference: DispatchSendTargetReferenceLoadResult;
}): {
  action: WakeLoopSpacePostMessageAction;
  tracePayload: {
    activationTargetsCount: number;
    ccTargetsCount: number;
    inputKind: DispatchPostMessageInputKind;
    memberDirectoryLoaded: boolean;
    referenceTargetsCount: number;
    replyToSignalIdResolved: boolean;
  };
} {
  const normalizedEnvelope = normalizeMessageEnvelope(
    input.action.messageEnvelope
  );
  const replyToSignalId = normalizeReplyToSignalId({
    currentReplyToSignalId: input.currentReplyToSignalId,
    envelopeReplyToSignalId: normalizedEnvelope.replyToSignalId ?? null,
  });

  const canonicalAction: WakeLoopSpacePostMessageAction = {
    ...input.action,
    messageEnvelope: {
      ...normalizedEnvelope,
      ...(replyToSignalId ? { replyToSignalId } : {}),
    },
  };

  validateDispatchPostMessageTargets({
    action: canonicalAction,
    sendTargetReference: input.sendTargetReference,
  });

  return {
    action: canonicalAction,
    tracePayload: {
      activationTargetsCount:
        canonicalAction.messageEnvelope.activationTargets.length,
      ccTargetsCount: canonicalAction.messageEnvelope.ccTargets.length,
      inputKind: input.inputKind,
      memberDirectoryLoaded: input.sendTargetReference.status === "loaded",
      referenceTargetsCount:
        canonicalAction.messageEnvelope.referenceTargets.length,
      replyToSignalIdResolved: Boolean(
        canonicalAction.messageEnvelope.replyToSignalId
      ),
    },
  };
}

function normalizeReplyToSignalId(input: {
  currentReplyToSignalId: string | null;
  envelopeReplyToSignalId: string | null;
}): string | null {
  if (!input.currentReplyToSignalId) {
    if (input.envelopeReplyToSignalId) {
      throw new Error(
        "post_message.messageEnvelope.replyToSignalId must match the current dispatch reply target"
      );
    }
    return null;
  }

  if (
    input.envelopeReplyToSignalId &&
    input.envelopeReplyToSignalId !== input.currentReplyToSignalId
  ) {
    throw new Error(
      "post_message.messageEnvelope.replyToSignalId must match the current dispatch reply target"
    );
  }

  return input.currentReplyToSignalId;
}

function validateDispatchPostMessageTargets(input: {
  action: WakeLoopSpacePostMessageAction;
  sendTargetReference: DispatchSendTargetReferenceLoadResult;
}): void {
  const { activationTargets, ccTargets, referenceTargets } =
    input.action.messageEnvelope;
  if (
    activationTargets.length === 0 &&
    referenceTargets.length === 0 &&
    ccTargets.length === 0
  ) {
    return;
  }

  if (input.sendTargetReference.status !== "loaded") {
    throw new Error(
      "post_message.messageEnvelope targets require a current Space member directory"
    );
  }

  const targetByProfileId = new Map(
    input.sendTargetReference.projection.map((target) => [
      target.profileId,
      target,
    ])
  );
  for (const rule of TARGET_VALIDATION_RULES) {
    const targets = input.action.messageEnvelope[rule.fieldName];
    for (const [index, targetProfileId] of targets.entries()) {
      validateDispatchTargetProfileId({
        fieldName: rule.fieldName,
        index,
        relationName: rule.relationName,
        targetByProfileId,
        targetProfileId,
      });
    }
  }
}

function validateDispatchTargetProfileId(input: {
  fieldName: MessageEnvelopeTargetField;
  index: number;
  relationName: "wake" | "reference" | "cc";
  targetByProfileId: ReadonlyMap<string, DispatchSendTargetProjectionEntry>;
  targetProfileId: string;
}): void {
  const target = input.targetByProfileId.get(input.targetProfileId);
  const fieldPath = `post_message.messageEnvelope.${input.fieldName}[${String(
    input.index
  )}]`;

  if (!target) {
    throw new Error(
      `${fieldPath} must reference an active profile in this space`
    );
  }

  if (input.relationName === "wake" && !target.allowedRelations.wake) {
    throw new Error(
      `${fieldPath} must reference an agent profile in this space`
    );
  }
  if (
    (input.relationName === "reference" || input.relationName === "cc") &&
    !target.allowedRelations[input.relationName]
  ) {
    throw new Error(
      `${fieldPath} must reference an active profile in this space`
    );
  }
}
